import { Link } from 'react-router-dom';
import Seo from '../components/Seo';

const LAST_UPDATED = '26 de octubre de 2026';

export default function Privacy() {
    return (
        <div data-testid="privacy-page">
            <Seo
                title="Aviso de Privacidad | Carpepel"
                description="Aviso de privacidad de Carpepel: qué datos personales recabamos, para qué los usamos, con quién los compartimos y cómo ejercer tus derechos ARCO."
                canonicalPath="/privacidad"
            />

            <div style={{ backgroundColor: 'var(--ink)' }} className="w-full py-10">
                <div className="max-w-3xl mx-auto px-6 md:px-12">
                    <p className="font-mono text-[11px] uppercase tracking-[0.22em] mb-3" style={{ color: 'var(--kraft)' }}>
                        Legal
                    </p>
                    <h1 className="font-heading text-3xl sm:text-4xl font-black text-white leading-[0.95]">
                        AVISO DE PRIVACIDAD
                    </h1>
                    <p className="text-sm mt-3" style={{ color: 'var(--ink-dim)' }}>
                        Última actualización: {LAST_UPDATED}
                    </p>
                </div>
            </div>

            <article className="max-w-3xl mx-auto px-6 md:px-12 py-12 md:py-16">
                <nav aria-label="Miga de pan" className="mb-10">
                    <ol className="flex items-center gap-2 flex-wrap font-mono text-[11px] uppercase tracking-[0.1em]">
                        <li><Link to="/" className="transition-opacity hover:opacity-70" style={{ color: 'var(--ink-soft)' }}>Inicio</Link></li>
                        <li aria-hidden="true" style={{ color: 'var(--line)' }}>/</li>
                        <li aria-current="page" style={{ color: 'var(--ink)' }}>Aviso de Privacidad</li>
                    </ol>
                </nav>

                <div className="space-y-8 text-[16px] leading-relaxed text-[var(--ink)]">
                    <p>
                        Este aviso describe cómo Carpepel ("nosotros") recaba, usa y protege los datos
                        personales de quienes visitan carpepel.com o realizan una compra, en cumplimiento
                        de la Ley Federal de Protección de Datos Personales en Posesión de los Particulares
                        (LFPDPPP).
                    </p>

                    <section>
                        <h2 className="font-heading text-xl font-bold text-[var(--ink)] mb-3">Responsable</h2>
                        <p>
                            Carpepel es operado por una persona física con actividad empresarial. Para
                            cualquier duda o solicitud relacionada con tus datos personales, puedes
                            contactarnos en{' '}
                            <a href="mailto:admin@carpepel.com" className="underline" style={{ color: 'var(--kraft-deep)' }}>
                                admin@carpepel.com
                            </a>.
                        </p>
                    </section>

                    <section>
                        <h2 className="font-heading text-xl font-bold text-[var(--ink)] mb-3">Datos que recabamos</h2>
                        <p className="mb-3">Dependiendo de cómo uses el sitio, podemos recabar:</p>
                        <ul className="space-y-2 pl-0">
                            {[
                                'Datos de identificación y contacto: nombre, correo electrónico, teléfono. Si compras sin cuenta, recibimos el nombre y el correo que escribes en la página de pago de Stripe.',
                                'Datos de envío: domicilio, colonia, ciudad, estado y código postal, necesarios para cotizar y entregar tu pedido. Si coordinas el envío por WhatsApp, los recibimos en esa conversación.',
                                'Datos de la cuenta (solo si decides crear una): nombre de usuario y contraseña (guardada siempre cifrada, nunca en texto plano).',
                                'Historial de compras: productos, cantidades, montos y estado de tus pedidos.',
                            ].map((item, i) => (
                                <li key={i} className="flex gap-3">
                                    <span className="mt-[10px] h-px w-4 flex-shrink-0" style={{ backgroundColor: 'var(--kraft-deep)' }} aria-hidden="true" />
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                        <p className="mt-3">
                            No recabamos datos personales sensibles (origen étnico, salud, religión,
                            preferencias, etc.).
                        </p>
                        <p className="mt-3">
                            Los datos de tu tarjeta de pago nunca llegan a nuestros servidores: el cobro
                            se procesa directamente por Stripe, una pasarela de pago certificada.
                        </p>
                    </section>

                    <section>
                        <h2 className="font-heading text-xl font-bold text-[var(--ink)] mb-3">Para qué usamos tus datos</h2>
                        <ul className="space-y-2 pl-0">
                            {[
                                'Procesar y dar seguimiento a tu pedido, incluyendo cotizar y generar tu guía de envío.',
                                'Contactarte sobre el estado de tu compra (correo y, si nos das tu teléfono, notificaciones del pedido).',
                                'Administrar tu cuenta y tu historial de compras.',
                                'Atender dudas y aclaraciones que nos envíes.',
                                'Cumplir obligaciones fiscales y legales.',
                            ].map((item, i) => (
                                <li key={i} className="flex gap-3">
                                    <span className="mt-[10px] h-px w-4 flex-shrink-0" style={{ backgroundColor: 'var(--kraft-deep)' }} aria-hidden="true" />
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                        <p className="mt-3">
                            No usamos tus datos para enviarte publicidad ni los vendemos a terceros.
                        </p>
                    </section>

                    <section>
                        <h2 className="font-heading text-xl font-bold text-[var(--ink)] mb-3">Con quién compartimos tus datos</h2>
                        <p className="mb-3">
                            Para operar el sitio y cumplir con tu pedido, compartimos únicamente los datos
                            necesarios con estos proveedores, quienes los tratan por nuestra cuenta:
                        </p>
                        <ul className="space-y-2 pl-0">
                            {[
                                ['Stripe', 'procesa el pago de tu pedido.'],
                                ['SkyDropX', 'cotiza tarifas de envío y genera la guía con la paquetería.'],
                                ['WhatsApp (Meta)', 'canal por el que coordinamos el envío cuando eliges pagar solo el papel; lo que escribas ahí lo procesa también WhatsApp según sus propias políticas.'],
                                ['Resend', 'envía los correos de confirmación y seguimiento de tu pedido.'],
                                ['Supabase', 'aloja de forma segura nuestra base de datos e imágenes de producto.'],
                            ].map(([name, desc], i) => (
                                <li key={i} className="flex gap-3">
                                    <span className="mt-[10px] h-px w-4 flex-shrink-0" style={{ backgroundColor: 'var(--kraft-deep)' }} aria-hidden="true" />
                                    <span><strong>{name}</strong> {desc}</span>
                                </li>
                            ))}
                        </ul>
                    </section>

                    <section>
                        <h2 className="font-heading text-xl font-bold text-[var(--ink)] mb-3">Cookies y analítica</h2>
                        <p>
                            Usamos Google Analytics (a través de Google Tag Manager) para entender cómo se
                            usa el sitio de forma agregada — por ejemplo, qué páginas se visitan más. Esto
                            implica que Google recibe datos de tu navegador (como tu dirección IP) a través
                            de cookies. No usamos esta información para identificarte personalmente.
                            Puedes bloquear estas cookies desde la configuración de tu navegador; el sitio
                            sigue funcionando igual sin ellas.
                        </p>
                    </section>

                    <section>
                        <h2 className="font-heading text-xl font-bold text-[var(--ink)] mb-3">Tus derechos (ARCO)</h2>
                        <p>
                            Tienes derecho a Acceder a tus datos personales, Rectificarlos si están
                            desactualizados o son inexactos, Cancelarlos cuando consideres que no se
                            requieren para las finalidades señaladas, y Oponerte a su uso para fines
                            específicos. Para ejercer cualquiera de estos derechos, escríbenos a{' '}
                            <a href="mailto:admin@carpepel.com" className="underline" style={{ color: 'var(--kraft-deep)' }}>
                                admin@carpepel.com
                            </a>{' '}
                            describiendo tu solicitud; te responderemos en un plazo razonable.
                        </p>
                    </section>

                    <section>
                        <h2 className="font-heading text-xl font-bold text-[var(--ink)] mb-3">Conservación de tus datos</h2>
                        <p>
                            Conservamos tus datos mientras tengas una cuenta activa o mientras sean necesarios
                            para las finalidades descritas arriba, y después el tiempo adicional que exijan
                            nuestras obligaciones fiscales y legales. Puedes solicitar la cancelación de tu
                            cuenta en cualquier momento escribiendo a{' '}
                            <a href="mailto:admin@carpepel.com" className="underline" style={{ color: 'var(--kraft-deep)' }}>
                                admin@carpepel.com
                            </a>.
                        </p>
                    </section>

                    <section>
                        <h2 className="font-heading text-xl font-bold text-[var(--ink)] mb-3">Seguridad de tus datos</h2>
                        <p>
                            Aplicamos medidas de seguridad razonables para proteger tus datos personales
                            contra daño, pérdida, alteración, acceso o uso no autorizado. Ningún sitio puede
                            garantizar seguridad absoluta; si detectamos una vulneración que pueda afectar tus
                            derechos, te lo notificaremos conforme a lo que exige la ley.
                        </p>
                    </section>

                    <section>
                        <h2 className="font-heading text-xl font-bold text-[var(--ink)] mb-3">Menores de edad</h2>
                        <p>
                            Carpepel vende a negocios y no está dirigido a menores de edad. No recabamos a
                            sabiendas datos personales de menores de edad; si tienes conocimiento de que un
                            menor nos proporcionó datos personales, contáctanos para eliminarlos.
                        </p>
                    </section>

                    <section>
                        <h2 className="font-heading text-xl font-bold text-[var(--ink)] mb-3">Cambios a este aviso</h2>
                        <p>
                            Podemos actualizar este aviso si cambian nuestras prácticas o la normatividad
                            aplicable. La fecha de la versión vigente siempre aparece al inicio de esta
                            página. Las condiciones de compra —precios, pago, envío y devoluciones— están en
                            nuestros{' '}
                            <Link to="/terminos" className="underline" style={{ color: 'var(--kraft-deep)' }}>
                                Términos y Condiciones
                            </Link>.
                        </p>
                    </section>
                </div>
            </article>
        </div>
    );
}
